import React, { useState, useMemo } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const ReportFilters = ({ reports, onFiltersChange, style }) => {
  const [selectedFilters, setSelectedFilters] = useState({});
  const [isExpanded, setIsExpanded] = useState(false);

  // Extraer categorías y valores únicos de los reportes
  const filterOptions = useMemo(() => {
    const categories = {};
    
    reports.forEach(report => {
      if (report.tags && report.tags.length > 0) {
        report.tags.forEach(tag => {
          const category = tag.category;
          if (!categories[category]) {
            categories[category] = {
              values: new Set(),
              color: tag.color
            };
          }
          categories[category].values.add(tag.value);
        });
      }
    });

    // Convertir Sets a arrays y ordenar
    Object.keys(categories).forEach(category => {
      categories[category].values = Array.from(categories[category].values).sort();
    });

    return categories;
  }, [reports]);

  const categoryLabels = {
    'sector': 'Sector',
    'cuartel': 'Cuartel', 
    'cultivo': 'Cultivo',
    'estado': 'Estado',
    'zona': 'Zona',
    'edificio': 'Edificio',
    'material': 'Material',
    'tramo': 'Tramo',
    'tipo': 'Tipo',
    'condicion': 'Condición',
    'perimetro': 'Perímetro',
    'nivel': 'Nivel'
  };

  const handleFilterToggle = (category, value) => {
    const newFilters = { ...selectedFilters };
    
    if (!newFilters[category]) {
      newFilters[category] = [];
    }
    
    const index = newFilters[category].indexOf(value);
    if (index > -1) {
      newFilters[category].splice(index, 1);
      if (newFilters[category].length === 0) {
        delete newFilters[category];
      }
    } else {
      newFilters[category].push(value);
    }
    
    setSelectedFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    setSelectedFilters({});
    onFiltersChange({});
  };

  const hasActiveFilters = Object.keys(selectedFilters).length > 0;
  const filterCount = Object.values(selectedFilters).reduce((sum, values) => sum + values.length, 0);

  if (Object.keys(filterOptions).length === 0) {
    return null;
  }

  return (
    <View style={[styles.container, style]}>
      <TouchableOpacity 
        style={styles.header}
        onPress={() => setIsExpanded(!isExpanded)}
      >
        <View style={styles.headerLeft}>
          <Ionicons name="filter-outline" size={20} color="#3498db" />
          <Text style={styles.headerTitle}>Filtros</Text>
          {filterCount > 0 && (
            <View style={styles.filterBadge}>
              <Text style={styles.filterBadgeText}>{filterCount}</Text>
            </View>
          )}
        </View>
        <View style={styles.headerRight}>
          {hasActiveFilters && (
            <TouchableOpacity onPress={clearAllFilters} style={styles.clearButton}>
              <Text style={styles.clearButtonText}>Limpiar</Text>
            </TouchableOpacity>
          )}
          <Ionicons 
            name={isExpanded ? 'chevron-up' : 'chevron-down'} 
            size={20} 
            color="#7f8c8d" 
          />
        </View>
      </TouchableOpacity>

      {isExpanded && (
        <ScrollView style={styles.filtersContent} showsVerticalScrollIndicator={false}>
          {Object.entries(filterOptions).map(([category, data]) => (
            <View key={category} style={styles.categorySection}>
              <Text style={styles.categoryTitle}>
                {categoryLabels[category] || category}
              </Text>
              <View style={styles.valuesContainer}>
                {data.values.map(value => {
                  const isSelected = selectedFilters[category]?.includes(value);
                  return (
                    <TouchableOpacity
                      key={value}
                      style={[
                        styles.valueTag,
                        { 
                          backgroundColor: isSelected ? data.color : '#f8f9fa',
                          borderColor: data.color
                        }
                      ]}
                      onPress={() => handleFilterToggle(category, value)}
                    >
                      <Text style={[
                        styles.valueText,
                        { color: isSelected ? 'white' : data.color }
                      ]}>
                        {value}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          ))}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    borderRadius: 12,
    marginBottom: 15,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 15,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginLeft: 8,
  },
  filterBadge: {
    backgroundColor: '#e74c3c',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 8,
  },
  filterBadgeText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  clearButton: {
    marginRight: 10,
  },
  clearButtonText: {
    color: '#e74c3c',
    fontSize: 14,
    fontWeight: '500',
  },
  filtersContent: {
    maxHeight: 300,
    paddingHorizontal: 15,
    paddingBottom: 15,
  },
  categorySection: {
    marginBottom: 20,
  },
  categoryTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#34495e',
    marginBottom: 8,
  },
  valuesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  valueTag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginRight: 8,
    marginBottom: 8,
    borderWidth: 1,
  },
  valueText: {
    fontSize: 12,
    fontWeight: '500',
  },
});

export default ReportFilters;